/* eslint-disable eqeqeq */
import React, { useState, useEffect, useRef } from "react";
import api from "../api"; 
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// --- HELPER: Safely get User ID (handles 'id' vs 'userId') ---
const getSafeId = (user) => {
    if (!user) return null;
    return user.id || user.userId || null;
};

// --- SEARCHABLE DROPDOWN COMPONENT ---
const SearchableUserDropdown = ({ label, onSelect, placeholder, excludeIds = [] }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSearch = async (searchTerm) => {
        setQuery(searchTerm);
        if (searchTerm.length < 2) {
            setResults([]);
            return;
        }
        try {
            const res = await api.get(`/vres/projects/users/search?query=${searchTerm}`);
            const filtered = res.data.filter(u => !excludeIds.includes(Number(getSafeId(u))));
            setResults(filtered);
            setIsOpen(true);
        } catch (error) {
            console.error("Search failed", error);
        }
    };

    const handleSelect = (user) => {
        const displayName = `${user.name} (${user.email})`;
        setQuery(displayName); 
        setResults([]);
        setIsOpen(false);
        onSelect(user); 
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        onSelect(null); 
        handleSearch(val);
    };

    return (
        <div style={{ marginBottom: '10px', position: 'relative', flex: 1 }} ref={wrapperRef}>
            {label && <label style={styles.label}>{label}</label>}
            <input
                type="text"
                className="cp-input"
                style={styles.input}
                placeholder={placeholder || "Search User..."}
                value={query}
                onChange={handleInputChange}
                onFocus={() => { if(query.length >= 2) setIsOpen(true); }}
            />
            {isOpen && results.length > 0 && (
                <ul style={styles.dropdownList}>
                    {results.map((user) => (
                        <li 
                            key={getSafeId(user)} 
                            style={styles.dropdownItem}
                            onMouseDown={() => handleSelect(user)} 
                        >
                            <strong>{user.name}</strong> <br/>
                            <small>{user.email}</small>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const CreateProjectPage = () => {
  const navigate = useNavigate();
  
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  
  const [currentMaker, setCurrentMaker] = useState(null);
  const [currentChecker, setCurrentChecker] = useState(null);
  const [currentIssuer, setCurrentIssuer] = useState(null);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [currentObserver, setCurrentObserver] = useState(null);

  const [approverPairs, setApproverPairs] = useState([]); 
  const [assignedIssuers, setAssignedIssuers] = useState([]); 
  const [assignedObservers, setAssignedObservers] = useState([]); 
  const [assignedVendors, setAssignedVendors] = useState([]); 

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      try {
        const currentProjectId = localStorage.getItem("selectedProjectId");
        if (!currentProjectId) {
            toast.error("No project selected.");
            navigate("/select-project");
            return;
        }

        const projectRes = await api.get(`/vres/projects/${currentProjectId}`);
        setProjectName(projectRes.data.title || "");
        setDescription(projectRes.data.description || "");
        if (projectRes.data.description) setIsUpdateMode(true);

        try {
            const teamRes = await api.get(`/vres/projects/${currentProjectId}/team-details`);
            const teamData = teamRes.data || {};
            if (teamData.departments) setApproverPairs(teamData.departments);
            if (teamData.issuers) setAssignedIssuers(teamData.issuers.map(u => ({ userId: u.id, name: u.name, email: u.email })));
            if (teamData.observers) setAssignedObservers(teamData.observers.map(u => ({ userId: u.id, name: u.name, email: u.email })));
            if (teamData.vendors) setAssignedVendors(teamData.vendors.map(v => ({ userId: v.id, name: v.name, gst: v.vendorGst || "", address: v.vendorAddress || "" })));
        } catch(e) { console.log("No existing team details."); }
      } catch (err) {
        console.error("Error initializing:", err);
      } finally { setLoading(false); }
    };
    initializePage();
  }, [navigate]);

  const getBusyIds = () => {
      return [
          ...approverPairs.flatMap(p => [Number(p.makerId), Number(p.checkerId)]),
          ...assignedIssuers.map(u => Number(u.userId)),
          ...assignedObservers.map(u => Number(u.userId)),
          ...assignedVendors.map(u => Number(u.userId)),
          getSafeId(currentMaker), getSafeId(currentChecker), getSafeId(currentIssuer), getSafeId(currentVendor), getSafeId(currentObserver)
      ].filter(Boolean).map(Number);
  };

  // --- Handlers ---
  const handleAddApproverPair = () => {
    if (!currentMaker || !currentChecker) return toast.warn("Select both Maker and Checker.");
    
    const makerId = getSafeId(currentMaker);
    const checkerId = getSafeId(currentChecker);

    if (!makerId || !checkerId) return toast.error("Invalid Maker or Checker selected.");
    if (Number(makerId) === Number(checkerId)) return toast.warn("Maker and Checker cannot be the same.");
    
    setApproverPairs(prev => [...prev, { 
        makerId: makerId, makerName: currentMaker.name, 
        checkerId: checkerId, checkerName: currentChecker.name 
    }]);
    setCurrentMaker(null); 
    setCurrentChecker(null);
  };

  const handleRemoveApprover = (index) => setApproverPairs(prev => prev.filter((_, i) => i !== index));

  const handleAddIssuer = () => {
      if (!currentIssuer) return toast.warn("Select an Issuer.");
      const uid = getSafeId(currentIssuer);
      if (!uid) return toast.error("Selected user has no valid ID.");

      setAssignedIssuers(prev => [...prev, { userId: uid, name: currentIssuer.name, email: currentIssuer.email }]);
      setCurrentIssuer(null);
  };
  const handleRemoveIssuer = (index) => setAssignedIssuers(prev => prev.filter((_, i) => i !== index));

  const handleAddObserver = () => {
      if (!currentObserver) return toast.warn("Select an Observer.");
      const uid = getSafeId(currentObserver);
      if (!uid) return toast.error("Selected user has no valid ID.");

      setAssignedObservers([{ userId: uid, name: currentObserver.name, email: currentObserver.email }]);
      setCurrentObserver(null);
  };
  const handleRemoveObserver = () => setAssignedObservers([]);

  // ✅ UPDATED: Pre-fill GST and Address from search result
  const handleAddVendor = () => {
      if (!currentVendor) return toast.warn("Select a Vendor.");
      const uid = getSafeId(currentVendor);
      if (!uid) return toast.error("Selected user has no valid ID.");

      // currentVendor is the DTO returned from backend.
      // DTO has 'gst' and 'address'.
      // We assume DTO fields are named 'gst' and 'address'.
      const existingGst = currentVendor.gst || "";
      const existingAddress = currentVendor.address || "";

      setAssignedVendors(prev => [...prev, { 
          userId: uid, 
          name: currentVendor.name, 
          gst: existingGst, 
          address: existingAddress
      }]);
      setCurrentVendor(null);
  };

  const handleRemoveVendor = (index) => setAssignedVendors(prev => prev.filter((_, i) => i !== index));
  const handleVendorChange = (index, field, val) => {
      setAssignedVendors(prev => prev.map((v, i) => i === index ? { ...v, [field]: val } : v));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const currentProjectId = localStorage.getItem("selectedProjectId");
    
    const validIssuers = assignedIssuers.map(u => Number(u.userId)).filter(id => id > 0);
    const validVendors = assignedVendors
        .filter(v => v.userId && Number(v.userId) > 0)
        .map(v => ({ userId: Number(v.userId), gst: v.gst, address: v.address }));
    
    const obsId = (assignedObservers.length > 0 && Number(assignedObservers[0].userId) > 0) 
                  ? Number(assignedObservers[0].userId) 
                  : null;

    const payload = {
      projectName,
      projectDescription: description,
      approvers: approverPairs.map(p => ({ makerId: Number(p.makerId), checkerId: Number(p.checkerId) })),
      issuerIds: validIssuers,
      observerId: obsId,
      vendors: validVendors,
    };

    console.log("Submitting Payload:", payload); 

    try {
      await api.put(`/vres/projects/${currentProjectId}/details`, payload);
      toast.success("Project details saved successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const PageStyleBlock = () => (
    <style>{`
      .cp-input:focus, .cp-textarea:focus { border-color: #20c997 !important; outline: none; }
      .cp-btn:hover:not(:disabled) { opacity: 0.9; }
      .cp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
      .cp-readonly { background-color: #e9ecef; cursor: not-allowed; color: #6c757d; }
    `}</style>
  );

  if (loading && !projectName) return <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>;

  return (
    <div style={styles.formWrapper}>
      <PageStyleBlock />
      <div style={styles.formCard}>
        <h2 style={styles.formTitle}>{isUpdateMode ? "Update Project Details" : "Define Project Details"}</h2>
        <form onSubmit={handleSubmit}>
          {/* 1. INFO */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>1. Project Information</h3>
            <div style={styles.formGrid}>
              <div style={styles.fullWidth}>
                <label style={styles.label}>Project Name</label>
                <input style={styles.input} className="cp-input cp-readonly" value={projectName} readOnly />
              </div>
              <div style={styles.fullWidth}>
                <label style={styles.label}>Description</label>
                <textarea style={styles.textarea} className="cp-textarea" value={description} onChange={(e) => setDescription(e.target.value)} required />
              </div>
            </div>
          </div>

          {/* 2. DEPARTMENTS */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>2. Departments (Maker & Checker)</h3>
            <div style={styles.tagContainer}>
              {approverPairs.map((pair, index) => (
                <div style={styles.tag} key={index}>
                  <span><strong>{pair.makerName}</strong> & <strong>{pair.checkerName}</strong></span>
                  <span style={styles.removeBtn} onClick={() => handleRemoveApprover(index)}>✕</span>
                </div>
              ))}
            </div>
            <div style={styles.dropdownGroup}>
              <SearchableUserDropdown placeholder="Search Maker..." onSelect={setCurrentMaker} excludeIds={getBusyIds()} />
              <SearchableUserDropdown placeholder="Search Checker..." onSelect={setCurrentChecker} excludeIds={getBusyIds()} />
              <button type="button" style={styles.btnCombine} className="cp-btn" onClick={handleAddApproverPair}>Combine</button>
            </div>
          </div>

          {/* 3. ISSUERS */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>3. Issuers</h3>
            <div style={styles.tagContainer}>
              {assignedIssuers.map((u, index) => (
                <div style={styles.tag} key={index}>
                  <span>{u.name} <small>({u.email})</small></span>
                  <span style={styles.removeBtn} onClick={() => handleRemoveIssuer(index)}>✕</span>
                </div>
              ))}
            </div>
            <div style={styles.dropdownGroup}>
                <SearchableUserDropdown placeholder="Search Issuer..." onSelect={setCurrentIssuer} excludeIds={getBusyIds()} />
                <button type="button" style={styles.btnCombine} className="cp-btn" onClick={handleAddIssuer}>Add</button>
            </div>
          </div>

          {/* 4. OBSERVER */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>4. Observer</h3>
            <div style={styles.tagContainer}>
              {assignedObservers.map((u, index) => (
                <div style={styles.tag} key={index}>
                  <span>{u.name} <small>({u.email})</small></span>
                  <span style={styles.removeBtn} onClick={handleRemoveObserver}>✕</span>
                </div>
              ))}
            </div>
            {assignedObservers.length === 0 && (
                <div style={styles.dropdownGroup}>
                    <SearchableUserDropdown placeholder="Search Observer..." onSelect={setCurrentObserver} excludeIds={getBusyIds()} />
                    <button type="button" style={styles.btnCombine} className="cp-btn" onClick={handleAddObserver}>Set</button>
                </div>
            )}
          </div>

          {/* 5. VENDORS */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>5. Vendors</h3>
            <div style={{marginBottom: '15px'}}>
              {assignedVendors.map((vendor, index) => (
                <div key={index} style={styles.vendorRow}>
                  <strong style={{ minWidth: "150px", fontSize: "0.9rem" }}>{vendor.name}</strong>
                  <input style={styles.input} placeholder="GST Number" value={vendor.gst} onChange={(e) => handleVendorChange(index, "gst", e.target.value)} required />
                  <input style={styles.input} placeholder="Address" value={vendor.address} onChange={(e) => handleVendorChange(index, "address", e.target.value)} required />
                  <span style={{ ...styles.removeBtn, fontSize: "18px" }} onClick={() => handleRemoveVendor(index)}>✕</span>
                </div>
              ))}
            </div>
            <div style={styles.dropdownGroup}>
              <SearchableUserDropdown placeholder="Search Vendor..." onSelect={setCurrentVendor} excludeIds={getBusyIds()} />
              <button type="button" style={styles.btnCombine} className="cp-btn" onClick={handleAddVendor}>Add Vendor</button>
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button type="button" className="cp-btn" style={styles.btnSecondary} onClick={() => navigate("/dashboard")}>Cancel</button>
            <button type="submit" className="cp-btn" style={styles.btnPrimary} disabled={loading}>{loading ? "Processing..." : "Save Project"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  formWrapper: { padding: '30px', display: 'flex', justifyContent: 'center', width: '100%', minHeight: '100%', fontFamily: "'Segoe UI', Roboto, sans-serif", backgroundColor: '#f0fdf9' },
  formCard: { background: 'white', width: '100%', maxWidth: '850px', padding: '30px 40px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', borderTop: '5px solid #20c997', margin: '20px auto' },
  formTitle: { textAlign: 'center', marginBottom: '25px', fontSize: '1.5rem', color: '#007bff', fontWeight: '700' },
  section: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f1f3f5' },
  sectionTitle: { color: "#2d3436", marginBottom: "12px", fontSize: "1.1rem", fontWeight: "600" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr", gap: "15px" },
  fullWidth: { gridColumn: "1 / -1" },
  label: { display: "block", marginBottom: "6px", fontWeight: "600", color: "#2d3436", fontSize: "0.9rem" },
  input: { width: "100%", padding: "10px 12px", border: "1px solid #dcdcdc", borderRadius: "6px", fontSize: "0.9rem", boxSizing: "border-box", color: "#2d3436" },
  textarea: { width: "100%", padding: "10px 12px", border: "1px solid #dcdcdc", borderRadius: "6px", minHeight: "80px", resize: "vertical", boxSizing: "border-box", fontSize: "0.9rem", color: "#2d3436" },
  dropdownGroup: { display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "10px" },
  tagContainer: { display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "15px" },
  tag: { backgroundColor: "#e9ecef", borderRadius: "20px", padding: "6px 12px", display: "flex", alignItems: "center", fontSize: "0.85rem", color: "#495057", fontWeight: "500", border: '1px solid #dee2e6' },
  removeBtn: { marginLeft: "10px", cursor: "pointer", fontWeight: "bold", color: "#dc3545" },
  vendorRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "6px" },
  buttonGroup: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "30px" },
  btnCombine: { padding: "9px 20px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", backgroundColor: "#20c997", color: "#fff", fontSize: "0.9rem", height: "38px", whiteSpace: "nowrap" },
  btnPrimary: { padding: "10px 24px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", backgroundColor: "#007bff", color: "#fff", fontSize: "0.95rem" },
  btnSecondary: { padding: "10px 24px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", backgroundColor: "#6c757d", color: "#fff", fontSize: "0.95rem" },
  dropdownList: { position: 'absolute', top: '100%', left: 0, right: 0, border: '1px solid #dcdcdc', borderRadius: '0 0 6px 6px', backgroundColor: 'white', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', listStyle: 'none', padding: 0, margin: 0, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  dropdownItem: { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.9rem', backgroundColor: '#fff' }
};

export default CreateProjectPage;